// Studby — signup page (starts the 3-day free trial).
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sprout } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await signup(name, email, password);
      navigate("/app/subscribe");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-background px-5">
      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <span className="grid place-items-center w-10 h-10 rounded-lg bg-primary text-primary-foreground">
            <Sprout size={22} aria-hidden="true" />
          </span>
          <span className="font-heading font-extrabold text-2xl text-primary">Studby</span>
        </Link>

        <div className="bg-card border border-border rounded-2xl p-7">
          <h1 className="font-heading text-2xl font-bold mb-1">Start your free trial</h1>
          <p className="text-sm text-muted-foreground mb-6">
            3 days free — 5 questions a day, then from €3.99/mo.
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name" required value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="signup-name-input" placeholder="Alex"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="signup-email-input" placeholder="you@example.com"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password" type="password" required minLength={6} value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="signup-password-input" placeholder="At least 6 characters"
              />
            </div>
            {error && (
              <p className="text-sm text-destructive" data-testid="signup-error">{error}</p>
            )}
            <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="signup-submit-button">
              {loading ? "Creating account…" : "Start free trial"}
            </Button>
          </form>
        </div>

        <p className="text-center text-sm text-muted-foreground mt-5">
          Already have an account?{" "}
          <Link to="/login" className="text-primary font-semibold" data-testid="go-to-login">
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
}
